import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";

const dur = (s) => (!s ? "just opened" : s < 60 ? `${s}s` : `${Math.floor(s / 60)} min`);

export function useLiveReads(intervalMs = 30000) {
  const [reads, setReads] = useState([]);
  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/proposals/live-reads").then((r) => { if (alive) setReads(r.data?.reads || []); }).catch(() => {});
    tick();
    const t = setInterval(tick, intervalMs);
    return () => { alive = false; clearInterval(t); };
  }, [intervalMs]);
  return reads;
}

// A quiet strip on Today: who is reading a proposal right now (heartbeat in the last two minutes).
export default function LiveReads() {
  const reads = useLiveReads();
  const navigate = useNavigate();
  if (!reads.length) return null;
  return (
    <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-2.5 space-y-1.5" data-testid="live-reads">
      {reads.map((r) => (
        <button key={r.view_id} onClick={() => navigate(`/bids/${r.bid_id}?tab=proposal`)} data-testid={`live-read-${r.bid_id}`}
          className="w-full flex items-center gap-3 text-left text-sm text-emerald-900 hover:underline">
          <span className="relative flex h-2.5 w-2.5 shrink-0"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>
          <Eye className="w-4 h-4 text-emerald-600 shrink-0" />
          <span className="min-w-0 truncate"><b>{r.client_name || "The client"}</b> is reading the <b>{r.name}</b> proposal right now · {dur(r.duration_s)}{r.pages ? ` · ${r.pages} section${r.pages === 1 ? "" : "s"}` : ""}{r.city ? ` · ${r.city}` : ""}{r.device ? ` · ${r.device}` : ""}</span>
        </button>
      ))}
    </div>
  );
}

// Header dot: green pulse while any proposal is being read; click → Today.
export function LiveReadsDot() {
  const reads = useLiveReads(60000);
  const navigate = useNavigate();
  if (!reads.length) return null;
  return (
    <button onClick={() => navigate("/today")} data-testid="live-reads-dot" title={`${reads.length} proposal${reads.length === 1 ? "" : "s"} being read right now`}
      className="relative p-1.5 rounded-md text-emerald-300 hover:bg-white/10">
      <Eye className="w-4 h-4" />
      <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" /></span>
    </button>
  );
}
